// Import necessary types and dependencies
import { useLocalization } from '../hooks';
import { useAnalyticsTracking } from './analyticsContext';

// Define the DynamicEvent and AnalyticsItem interfaces
interface DynamicEvent {
    eventName: string;
    customEventName?: string;
    analyticItems: AnalyticsItem[];
}

interface AnalyticsItem {
    name: string;
    value: string;
}

export const useTracking = () => {
    const i18n = useLocalization();
    const analytics = useAnalyticsTracking();
    const onTriggerTracking = ({
        event,
        data,
        useAnalytics,
        dynamicEvents,
        selectedTabValue,
        selectedValueName,
        managerNameHtml,
        fundName,
        filterAnalyticLabel,
        toggleSwitch,
        analyticsCustomValue,
        selectedTextFilter,
        filterValue,
        useLowerCase,
    }: {
        event: string;
        selectedTabValue?: string;
        selectedValueName?: string;
        managerNameHtml?: string;
        fundName?: string;
        filterAnalyticLabel?: string;
        toggleSwitch?: string;
        data?: any;
        useAnalytics?: boolean;
        dynamicEvents?: DynamicEvent[];
        analyticsCustomValue?: string;
        selectedTextFilter?: string;
        filterValue?: string;
        useLowerCase?: boolean;
    }) => {
        // match event name and get its dynamicEvents
        const currentDynamicEvents = dynamicEvents?.find((item: any) => item.eventName === event);
        const updatedDynamicEvents = [];
        // Check if currentDynamicEvents is defined and an object
        if (typeof currentDynamicEvents === 'object' && currentDynamicEvents !== null) {
            // Convert the object properties into an array of dynamic events
            const analyticItems = currentDynamicEvents.analyticItems;
            const customEventName = currentDynamicEvents.customEventName;
            // Iterate over the array of dynamic events
            if (analyticItems && analyticItems.length > 0) {
                const updatedAnalyticItems = analyticItems.map((item: AnalyticsItem) => {
                    const selectedTab = selectedTabValue || '';
                    const modifiedItemValue = item.value
                        .replace('{{selectedTabValue}}', i18n.format(selectedTab, data))
                        .replace('{{selectedValue}}', i18n.format(selectedValueName, data))
                        .replace('{{managerName}}', i18n.format(managerNameHtml, data))
                        .replace('{{fundName}}', i18n.format(fundName, data))
                        .replace('{{filterAnalyticLabel}}', i18n.format(filterAnalyticLabel, data))
                        .replace('{{toggleSwitchValue}}', i18n.format(toggleSwitch, data))
                        .replace(
                            '{{analyticsCustomValue}}',
                            i18n.format(analyticsCustomValue, data)
                        )
                        .replace('{{selectedTextFilter}}', i18n.format(selectedTextFilter, data))
                        .replace('{{filterValue}}', i18n.format(filterValue, data));

                    // Modify the value property and return a new AnalyticsItem object
                    return {
                        ...item,
                        value: useLowerCase
                            ? i18n.format(modifiedItemValue, data).toLowerCase()
                            : i18n.format(modifiedItemValue, data),
                    };
                });

                // Return a new dynamicEvent object with updated analyticItems
                updatedDynamicEvents.push({
                    ...currentDynamicEvents,
                    eventName: customEventName || event,
                    analyticItems: updatedAnalyticItems,
                });
            } else {
                updatedDynamicEvents.push({ ...currentDynamicEvents });
            }
        }

        const trackingData = {
            dynamicEvents: updatedDynamicEvents,
        };

        // only track if dynamicEvents are available
        if (updatedDynamicEvents.length > 0 && useAnalytics) {
            analytics.track(trackingData);
        }
    };
    return onTriggerTracking;
};
